import { Button } from "@admin/components/atoms/Button";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  deleteControlPoint,
  deleteDish,
  deleteGood,
  deletePickles,
  deleteSupplier,
} from "@enos-monorepo/lib";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface DeleteMyListModalProps extends ModalProps {
  id: string;
  type: "supplier" | "processed" | "accepted" | "inventory" | "pickle";
  title?: string;
}

export function DeleteMyListModal({
  id,
  title,
  type,
  ...props
}: DeleteMyListModalProps) {
  const handleDeleteFn = (id: string) => {
    switch (type) {
      case "supplier":
        return deleteSupplier(id);
      case "processed":
        return deleteDish(id);
      case "accepted":
        return deleteGood(id);
      case "inventory":
        return deleteControlPoint(id);
      case "pickle":
        return deletePickles(id);
      default:
        return deleteSupplier(id);
    }
  };

  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const deleteMutation = useMutation({
    mutationFn: handleDeleteFn,
    onSuccess: () => {
      toast.success(
        t("common.Item added successfully", {
          item: t("food.Defect"),
        }),
      );
      queryClient.invalidateQueries({
        queryKey: [
          {
            supplier: "suppliersByCompany",
            accepted: "goodsByCompany",
            processed: "dishesByCompany",
            inventory: "foodControlPoints",
            pickle: "picklesByCompany",
          }[type],
        ],
      });
      props.onClose?.();
    },
    onError: () => {
      toast.error(
        t("common.Failed to add item", {
          item: t("food.Defect"),
        }),
      );
    },
  });

  return (
    <Modal
      {...props}
      title={t("food.Delete Item", { item: title })}
      actionButtons={
        <>
          <Button variant="secondary" type="button" onClick={props?.onClose}>
            {t("common.Discard")}
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={deleteMutation.isPending}
            loading={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate(id)}
          >
            {t("food.Delete Item", { item: title })}
          </Button>
        </>
      }
    >
      <h2>
        {t("common.Are you sure about delete Item", {
          item: title,
        })}
      </h2>
    </Modal>
  );
}
